import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import React, { useState, useContext, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Link,
  Grid,
  Button,
  Checkbox,
  TextField,
  Typography,
  FormHelperText,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import { AuthContext } from '../../helper/context/auth/authContext';
import toast from 'react-hot-toast';
import { useResponsive } from '../../helper/use-reponsive';

const LoginForm = ({ onForgetPwd, handleSendOTP, setEmail, email }) => {
  const { login } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const upLg = useResponsive('up', 'lg');

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const validationSchema = yup.object().shape({
    Email: yup.string().email('Invalid Email-Id').required('Email is required'),
    Password: yup.string().min(7, 'Password must be at least 7 characters').required('Password is required'),
  });

  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    setValue, 
    watch,
    trigger 
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      Email: '',
      Password: '',
    }
  });

  // Watch form values
  const formEmail = watch('Email');
  const formPassword = watch('Password');

  // Sync external email state with form
  useEffect(() => {
    if (email && email !== formEmail) {
      setValue('Email', email);
    }
  }, [email, setValue, formEmail]);

  // Load saved credentials on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberEmail');
    const savedPassword = localStorage.getItem("rememberPassword");
    const remember = localStorage.getItem('rememberMe') === 'true';
    
    if (remember && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setValue('Email', savedEmail);
      setValue('Password', savedPassword);
      setRememberMe(true);
      
      // Trigger validation for pre-filled fields
      setTimeout(() => {
        trigger(['Email', 'Password']);
      }, 100);
    }
  }, [setEmail, setValue, trigger]);

  const onSubmit = async (data) => {
    setLoading(true);
    
    // Update parent component email state
    setEmail(data.Email);
    
    await login(data);
    setLoading(false);

    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
      localStorage.setItem('rememberEmail', data.Email);
      localStorage.setItem('rememberPassword', data.Password);
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('rememberEmail');
      localStorage.removeItem('rememberPassword');
    }

    const userData = JSON.parse(localStorage.getItem('userData'));
    if (userData) {
      // Check for stored redirect URL after successful login
      const redirectUrl = localStorage.getItem("redirectAfterLogin");
      if (redirectUrl) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectUrl);
      } else if (userData[0]?.RoleName === 'Employee') {
        const Privilliages = JSON.parse(localStorage.getItem("Privilliages"));
        const filteredPrivilliages = Privilliages?.find(
          (item) => item.ModuleName === 'HRMS Dashboard'
        );
        if (filteredPrivilliages) navigate('/');
        else navigate('/view-employee');
      } else {
        navigate('/');
      }
    }
  };

  const redirectToHome = () => {
    if (localStorage.getItem('token')) {
      const userData = JSON.parse(localStorage.getItem('userData'));
      if (userData && userData[0]?.RoleName === 'Employee') {
        const Privilliages = JSON.parse(localStorage.getItem("Privilliages"));
        const filteredPrivilliages = Privilliages?.find(
          (item) => item.ModuleName === 'HRMS Dashboard'
        );
        if (filteredPrivilliages) navigate('/');
        else navigate('/view-employee');
      } else {
        navigate('/');
      }
    }
  };

  const userData = JSON.parse(localStorage.getItem('userData'));

  useEffect(() => {
    if (localStorage.getItem('token')) {
      // Check for stored redirect URL first
      const redirectUrl = localStorage.getItem("redirectAfterLogin");
      if (redirectUrl) {
        localStorage.removeItem("redirectAfterLogin");
        navigate(redirectUrl);
      } else if (userData && userData[0]?.RoleName === 'Employee') {
        const Privilliages = JSON.parse(localStorage.getItem("Privilliages"));
        const filteredPrivilliages = Privilliages?.find(
          (item) => item.ModuleName === 'HRMS Dashboard'
        );
        if (filteredPrivilliages) navigate('/');
        else navigate('/view-employee');
      } else {
        navigate('/');
      }
    } else {
      redirectToHome();
    }
  }, [navigate, userData]);

  useEffect(() => {
    if (localStorage.getItem("sessionExpired")) {
      toast.error("Token expired. Please log in again.");
      localStorage.removeItem("sessionExpired");
    }
  }, []);

  return (
    <Box p={3} className='block pt-10 w-11/12' sx={{ zoom: 1 }}>
      <Typography variant="h4" sx={{ justifyContent: "center", fontWeight: 600 }} className='text-gray-700 text-xl font-medium flex'>
        Login
      </Typography>

  <form className="mt-7" method="post" action="#" onSubmit={handleSubmit(onSubmit)}>
        <Box>
          <TextField
            label="Email"
            size="small"
            variant="standard"
            {...register('Email', {
              onChange: (e) => {
                setEmail(e.target.value);
              }
            })}
            fullWidth
            id="email"
            className="border-[#000000] border rounded-[5px]"
            sx={{
              "& .MuiInputBase-input": { color: "text.secondary" },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
                fontSize: "16px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#000000",
                  borderRadius: "5px",
                },
              },
            }}
          />
          {errors.Email && (
            <FormHelperText sx={{ color: "error.main" }}>
              {errors.Email.message}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ marginTop: "20px" }}>
          <TextField
            label="Password"
            variant="standard"
            size="small"
            type={showPassword ? 'text' : 'password'}
            {...register("Password")}
            fullWidth
            id="password"
            className="border-[#000000] border rounded-[5px]"
            sx={{
              "& .MuiInputBase-input": { color: "text.secondary" },
              "& .MuiInputLabel-root": {
                fontWeight: 600,
                fontSize: "16px",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "#000000",
                  borderRadius: "5px",
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    sx={{ width: 32, height: 32, padding: 0 }}
                  >
                    {showPassword ? (
                      <Visibility sx={{ fontSize: 18 }} />
                    ) : (
                      <VisibilityOff sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors.Password && (
            <FormHelperText sx={{ color: "error.main" }}>
              {errors.Password.message}
            </FormHelperText>
          )}
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={6} sm={6} className="flex items-center check-forgot pt-2" sx={{ mt: 3.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: "primary",
                    p: 0.2,
                    "& .MuiSvgIcon-root": {
                      fontSize: upLg ? "1rem" : "0.85rem",
                      ml: '5px'
                    },
                  }}
                />
              }
              label="Remember me"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: upLg ? "0.75rem" : "0.6rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={6} sm={6} container justifyContent="flex-end" className="pt-6">
            <Link
              onClick={onForgetPwd}
              underline="always"
              sx={{
                cursor: 'pointer',
                mt: 2,
                fontSize: upLg ? "0.75rem" : "0.6rem",
              }}
            >
              Forgot password?
            </Link>
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              className="w-full"
              style={{
                background: "primary",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: '10px',
                height: '30px'
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "#fff" }} />
              ) : (
                "Login"
              )}
            </Button>
            <Divider
              sx={{
                "& span": {
                  fontSize: upLg ? "0.75rem" : "0.6rem",
                },
              }}
            >
              OR
            </Divider>
            <Button
              variant="contained"
              className="w-full"
              onClick={handleSendOTP}
              style={{
                marginTop: 10,
                background: "primary",
                color: "#fff",
                height: '30px',
                fontSize: '12px',
              }}
            >
              Login with Email OTP
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default LoginForm;